/**
 * feat-107 shared primitive (4b/4) — Notion curriculum parser interface (ADR 0022).
 *
 * Pure TS, NO network. Turns a pasted Notion doc into the inferred
 * Drill › Question/Rehearsal hierarchy that importer Step 2 renders and Step 3
 * commits through the existing curriculum APIs. The Lesson (lvl 1) is chosen by
 * the user on Step 1, NOT parsed — the parser only produces Drills and their
 * children (so `parse()` returns `drills` + flat `items`, never a Lesson).
 *
 * THE TYPES + `parse` SIGNATURE + Needs-review rule set BELOW ARE FINAL.
 * The body is a stub — Slice B implements it and writes the golden test against
 * NOTION_IMPORT_TEMPLATE (must yield 2 Drills / 2 MC / 2 Fill / 1 Rehearsal / 1 Reading (feat-343) /
 * zero Needs-review). Slice A does not import this module.
 *
 * Grammar (spec §Parser grammar):
 *   `## Heading`              → new Drill (title = heading text)
 *   `1.` / `2)` numbered line → a Question (text follows the number)
 *   `A) B) C) D)` lines       → MC (or fill-blank) options
 *   `Correct: B`              → correct_option (MC + fill-blank)
 *   `Praise: <text>`          → correct_explanation (right-answer praise) [feat-109]
 *   `Explain: <text>`         → explanation (wrong-answer explanation)   [feat-109]
 *   `___` (3+ underscores)    → makes the question a Fill-blank
 *   `Accept: a, b, c`         → fill-blank EXTRA accepted variants (comma-split)
 *   `Rehearsal: <name>`       → a Rehearsal
 *   `Role:` / `Scenario:`     → rehearsal role / scenario_text
 *   `Correct example: <text>` → rehearsal correct_example_response  [feat-187]
 *     Matched only inside an open Rehearsal, and BEFORE the MC `Correct:` rule
 *     in the line loop. The two can't collide anyway (`Correct:` requires a
 *     single a–d letter) but the order is deliberate, and both are proven.
 *   bullets under `Good things:` / `Bad things:` → criteria[] (good/bad);
 *     a `- <label>: <description>` bullet splits into label + description, while a
 *     plain `- <text>` bullet uses the text as BOTH label and description so the
 *     criterion description is never empty (backend requires ≥1 char). [feat-109]
 *   `Reading: <title>`        → a Reading (feat-343). OPENS A CAPTURE MODE: every
 *     following line is body, verbatim, and NO other rule fires, until the first
 *     of (1) a line that is exactly `---` (consumed, not body), (2) the next
 *     `Reading:` / `Rehearsal:` line, (3) a `## ` Drill heading (exactly two
 *     hashes — `#`/`###` are headings inside the body), (4) end of paste. The
 *     captured body is then run through `normalizeReadingMarkdown`.
 */

import type { CorrectOption, QuestionType } from './quizContentAdminService';
import { mcOptionsProblem } from '../utils/mcOptions';

// ============================================================
// Needs-review rule set (final) — drives amber chips + gates Commit
// ============================================================

/** Stable reason codes for low-confidence parses. The human-facing `message`
 *  is authored per-flag by the parser (specific, e.g. "no Correct: line — we
 *  guessed B"). */
export type NeedsReviewReason =
  | 'mc_no_correct_line'      // MC with no `Correct:` line (guessed or unguessed)
  | 'mc_option_no_letter'     // an option line had no clear A–D letter
  | 'mc_too_few_options'      // MC with fewer than 2 options
  | 'mc_options_gap'          // feat-346: MC options not contiguous from A (e.g. A, B, D with no C)
  | 'fill_no_accept'          // fill-blank with no `Accept:` line (legal, but flagged)
  | 'rehearsal_missing_role'  // rehearsal with no `Role:` (REQUIRED → 422 on commit)
  | 'rehearsal_unresolved_role' // feat-248: a `Role:` the tenant has no prompt-backed role for (→ hard 400 on commit)
  | 'rehearsal_missing_correct_example' // feat-187: no `Correct example:` line (legal, but the "Try saying" card ships empty)
  | 'reading_images_dropped'  // feat-343: `![](…)` lines stripped from a Reading body (warn — re-add in the editor)
  | 'reading_empty_body'      // feat-343: Reading body empty after normalization (block — backend requires ≥1 char)
  | 'missing_required_field'; // any other required API field empty

/**
 * Severity decides commit-gating behaviour. BOTH severities show an amber
 * "⚠ Needs review" chip and BOTH must be cleared before Commit enables (AC9 —
 * "Commit is disabled until every Needs-review item is resolved").
 *   - `block` — a required API field is empty/ambiguous (would 422, or we can't
 *     pick a value). Resolved only by SUPPLYING the field in Step 2.
 *   - `warn`  — the write is legal as-is but worth a look (e.g. fill-blank with
 *     no variants). Resolved by supplying the field OR an explicit "looks fine"
 *     dismiss in Step 2.
 * Mapping: fill_no_accept + rehearsal_missing_correct_example +
 * reading_images_dropped (feat-343) ⇒ `warn`; all other reasons ⇒ `block`. (feat-187: `correct_example_response` is a NULLABLE
 * column, so the write is legal without it — hence `warn`, which the author can
 * dismiss. A `block` here would be unclearable except by typing an example.)
 */
export type NeedsReviewSeverity = 'block' | 'warn';

export interface NeedsReviewFlag {
  reason: NeedsReviewReason;
  severity: NeedsReviewSeverity;
  /** Specific, human-facing copy for the amber chip / inline note. */
  message: string;
}

// ============================================================
// Parsed result types (final)
// ============================================================

/** A parsed MC/fill option. For fill-blank the parser synthesizes one option
 *  holding the canonical answer (see the API-shape note on ParsedQuestion). */
export interface ParsedOption {
  letter: CorrectOption; // 'a' | 'b' | 'c' | 'd'
  text: string;
}

export interface ParsedDrill {
  /** Stable client id, assigned at parse time, threaded review → commit so
   *  type-overrides / moves / drops and committed-id tracking all key off it. */
  tempId: string;
  title: string;
}

/**
 * API-shape note: at commit, fill-blank reuses the `option_a..d` +
 * `correct_option` shape (catalog comment in quizContentAdminService): the
 * correct option's text IS the canonical answer that fills the `___`, and
 * `blank_accepted_variants` carries the EXTRA variants (newline-delimited,
 * canonical answer NOT repeated). The parser stores variants pre-joined in
 * `blankAcceptedVariants`; the commit helper builds the option payload.
 */
export interface ParsedQuestion {
  kind: 'question';
  tempId: string;
  /** Which Drill this belongs to (matches ParsedDrill.tempId). */
  drillTempId: string;
  type: QuestionType; // 'multiple_choice' | 'fill_blank'
  question_text: string;
  /** MC: the parsed A–D options. Fill: the A–D options too when the author wrote
   *  them explicitly (feat-109); otherwise the legacy `Accept:`-derived canonical. */
  options: ParsedOption[];
  /** MC/Fill correct answer; null when unguessable (drives `mc_no_correct_line`).
   *  For fill-blank the correct option's text is the canonical answer. */
  correct_option: CorrectOption | null;
  /** Fill-blank extra accepted variants, newline-delimited (API shape), or null. */
  blankAcceptedVariants: string | null;
  /** feat-109: wrong-answer explanation (API `explanation`). `Explain:` line. */
  explanation: string;
  /** feat-109: right-answer praise (API `correct_explanation`). `Praise:` line. */
  correct_explanation: string;
  needsReview: NeedsReviewFlag[];
}

export interface ParsedCriterion {
  kind: 'good' | 'bad';
  label: string;
  description: string;
  position: number;
}

export interface ParsedRehearsal {
  kind: 'rehearsal';
  tempId: string;
  drillTempId: string;
  name: string;
  /** null when no `Role:` line — REQUIRED by createRehearsal (block flag). */
  role: string | null;
  scenario_text: string;
  /** feat-187: the `Correct example:` line → `rehearsal_template.
   *  correct_example_response` (the mobile "Try saying" card). Empty string when
   *  absent — same typing as `scenario_text`; the column is nullable, so an
   *  empty one raises a WARN, not a block. */
  correct_example_response: string;
  criteria: ParsedCriterion[];
  needsReview: NeedsReviewFlag[];
}

/**
 * feat-343: a Reading item (feat-166) — optional title + markdown body. The body
 * is already normalized into the subset the Reading preview + Flutter renderer
 * support (see `normalizeReadingMarkdown`). Images are never imported.
 */
export interface ParsedReading {
  kind: 'reading';
  tempId: string;
  drillTempId: string;
  /** `Reading: <title>` text; empty string when the author left it blank (legal — no flag). */
  title: string;
  /** Normalized markdown body. Empty ⇒ `reading_empty_body` block flag. */
  body: string;
  /** How many `![](…)` image lines normalization stripped (drives `reading_images_dropped`). */
  imagesDropped: number;
  needsReview: NeedsReviewFlag[];
}

export type ParsedItem = ParsedQuestion | ParsedRehearsal | ParsedReading;

export interface ParseResult {
  /** Inferred Drills, in document order. */
  drills: ParsedDrill[];
  /** Flat list of children; each carries `drillTempId`. */
  items: ParsedItem[];
  /** Convenience aggregate of every flag across all items (same objects). */
  needsReview: NeedsReviewFlag[];
}

// ============================================================
// Entry point (interface final; body owned by Slice B)
// ============================================================

// ============================================================
// Grammar matchers (line-level, run on the trimmed line)
// ============================================================

const RE_DRILL = /^#{2,}\s+(.+?)\s*$/;                 // `## Heading`
const RE_REHEARSAL = /^rehearsal\s*:\s*(.*)$/i;        // `Rehearsal: <name>`
const RE_READING = /^reading\s*:\s*(.*)$/i;            // `Reading: <title>` (feat-343)
// feat-343: inside an open Reading only an EXACTLY-two-hash heading is a Drill
// (outside a Reading RE_DRILL still accepts `##`+, unchanged).
const RE_READING_DRILL_TERMINATOR = /^##(?!#)\s+\S/;
const RE_READING_DIVIDER = /^---$/;
const RE_ROLE = /^role\s*:\s*(.*)$/i;                  // `Role: ...`
const RE_SCENARIO = /^scenario\s*:\s*(.*)$/i;          // `Scenario: ...`
// feat-187: `Correct example: ...` — the `\s+` between the two words is what
// keeps it disjoint from RE_CORRECT below (which needs `correct` then a colon).
const RE_CORRECT_EXAMPLE = /^correct\s+example\s*:\s*(.*)$/i;
const RE_GOOD = /^good\s+things\s*:?\s*$/i;            // `Good things:`
const RE_BAD = /^bad\s+things\s*:?\s*$/i;              // `Bad things:`
const RE_NUMBERED = /^(\d+)[.)]\s*(.*)$/;              // `1.` / `2)` question (text optional)
const RE_CORRECT = /^correct\s*:\s*([a-d])\b/i;        // `Correct: B`
const RE_ACCEPT = /^accept\s*:\s*(.*)$/i;              // `Accept: a, b, c`
const RE_PRAISE = /^praise\s*:\s*(.*)$/i;              // `Praise: right-answer praise`
const RE_EXPLAIN = /^explain\s*:\s*(.*)$/i;            // `Explain: wrong-answer explanation`
const RE_OPTION = /^([a-d])[).]\s+(.+)$/i;             // `A) text`
const RE_BULLET = /^[-*•]\s+(.+)$/;                    // `- bullet` (optional `label: description`)
const RE_BLANK = /_{3,}/;                              // `___` fill marker

const LETTERS: CorrectOption[] = ['a', 'b', 'c', 'd'];

// ============================================================
// Entry point
// ============================================================

/**
 * Parse a pasted Notion doc into the inferred hierarchy. Pure + synchronous.
 * Never throws on malformed input — an unrecognizable paste returns empty
 * `drills`/`items` (Step 2's "parse failed / unrecognized" state keys off that).
 */
export function parse(text: string): ParseResult {
  const drills: ParsedDrill[] = [];
  const items: ParsedItem[] = [];

  let drillSeq = 0;
  let itemSeq = 0;

  let currentDrill: ParsedDrill | null = null;
  let currentQuestion: ParsedQuestion | null = null;
  let currentRehearsal: ParsedRehearsal | null = null;
  let rehearsalSection: 'good' | 'bad' | null = null;
  /** feat-343: the open Reading (capture mode) and its raw body lines. */
  let currentReading: ParsedReading | null = null;
  const readingRaw = new Map<string, string[]>();

  /** Raw `Accept:` variants per question tempId, resolved into the canonical
   *  answer / extra variants during the finalization pass (feat-109). Kept
   *  separate from `options` so explicit A–D fill options stay authoritative. */
  const acceptScratch = new Map<string, string[]>();

  /** A Question/Rehearsal needs a Drill. Synthesize one when items appear before
   *  any `## Heading` (messy paste) so every child has a `drillTempId`. */
  const ensureDrill = (): ParsedDrill => {
    if (!currentDrill) {
      currentDrill = { tempId: `drill-${++drillSeq}`, title: 'Untitled Drill' };
      drills.push(currentDrill);
    }
    return currentDrill;
  };

  for (const rawLine of text.split('\n')) {
    const raw = rawLine.replace(/\r$/, '');
    const line = raw.trim();

    // feat-343: open-Reading capture mode. Only the four terminators end it;
    // every other line — numbered, `A)`, bullets, `Correct:` — is body text.
    if (currentReading) {
      if (RE_READING_DIVIDER.test(line)) {
        currentReading = null; // divider is consumed, not body
        continue;
      }
      if (
        RE_READING.test(line) ||
        RE_REHEARSAL.test(line) ||
        RE_READING_DRILL_TERMINATOR.test(line)
      ) {
        currentReading = null; // fall through: the terminator line parses normally
      } else {
        readingRaw.get(currentReading.tempId)!.push(raw);
        continue;
      }
    }

    // Drill heading — opens a new Drill, closes any open item.
    const mDrill = line.match(RE_DRILL);
    if (mDrill) {
      currentDrill = { tempId: `drill-${++drillSeq}`, title: mDrill[1].trim() };
      drills.push(currentDrill);
      currentQuestion = null;
      currentRehearsal = null;
      rehearsalSection = null;
      continue;
    }

    // Reading — opens a new Reading and enters capture mode (feat-343).
    const mReading = line.match(RE_READING);
    if (mReading) {
      const drill = ensureDrill();
      currentReading = {
        kind: 'reading',
        tempId: `item-${++itemSeq}`,
        drillTempId: drill.tempId,
        title: mReading[1].trim(),
        body: '',
        imagesDropped: 0,
        needsReview: [],
      };
      readingRaw.set(currentReading.tempId, []);
      items.push(currentReading);
      currentQuestion = null;
      currentRehearsal = null;
      rehearsalSection = null;
      continue;
    }

    // Rehearsal — opens a new Rehearsal item.
    const mReh = line.match(RE_REHEARSAL);
    if (mReh) {
      const drill = ensureDrill();
      currentRehearsal = {
        kind: 'rehearsal',
        tempId: `item-${++itemSeq}`,
        drillTempId: drill.tempId,
        name: mReh[1].trim(),
        role: null,
        scenario_text: '',
        correct_example_response: '',
        criteria: [],
        needsReview: [],
      };
      items.push(currentRehearsal);
      currentQuestion = null;
      rehearsalSection = null;
      continue;
    }

    // Rehearsal sub-fields (only meaningful inside an open Rehearsal).
    const mRole = line.match(RE_ROLE);
    if (mRole && currentRehearsal) {
      const role = mRole[1].trim();
      currentRehearsal.role = role.length ? role : null;
      continue;
    }
    const mScenario = line.match(RE_SCENARIO);
    if (mScenario && currentRehearsal) {
      currentRehearsal.scenario_text = mScenario[1].trim();
      continue;
    }
    // feat-187: `Correct example:` → the rehearsal's model answer. Deliberately
    // matched HERE — inside the open-Rehearsal block, ahead of the MC
    // `Correct: <a-d>` rule below — so the two can never contend for a line.
    const mCorrectExample = line.match(RE_CORRECT_EXAMPLE);
    if (mCorrectExample && currentRehearsal) {
      currentRehearsal.correct_example_response = mCorrectExample[1].trim();
      continue;
    }
    if (RE_GOOD.test(line) && currentRehearsal) {
      rehearsalSection = 'good';
      continue;
    }
    if (RE_BAD.test(line) && currentRehearsal) {
      rehearsalSection = 'bad';
      continue;
    }

    // Numbered line — opens a new Question (Fill if it carries a `___` marker).
    const mNum = line.match(RE_NUMBERED);
    if (mNum) {
      const drill = ensureDrill();
      const questionText = mNum[2].trim();
      currentQuestion = {
        kind: 'question',
        tempId: `item-${++itemSeq}`,
        drillTempId: drill.tempId,
        type: RE_BLANK.test(questionText) ? 'fill_blank' : 'multiple_choice',
        question_text: questionText,
        options: [],
        correct_option: null,
        blankAcceptedVariants: null,
        explanation: '',
        correct_explanation: '',
        needsReview: [],
      };
      items.push(currentQuestion);
      currentRehearsal = null;
      rehearsalSection = null;
      continue;
    }

    // Correct: <letter> — for the open question (MC OR fill-blank, feat-109).
    const mCorrect = line.match(RE_CORRECT);
    if (mCorrect && currentQuestion) {
      currentQuestion.correct_option = mCorrect[1].toLowerCase() as CorrectOption;
      continue;
    }

    // Praise: <text> — right-answer praise (correct_explanation). feat-109.
    const mPraise = line.match(RE_PRAISE);
    if (mPraise && currentQuestion) {
      currentQuestion.correct_explanation = mPraise[1].trim();
      continue;
    }

    // Explain: <text> — wrong-answer explanation. feat-109.
    const mExplain = line.match(RE_EXPLAIN);
    if (mExplain && currentQuestion) {
      currentQuestion.explanation = mExplain[1].trim();
      continue;
    }

    // Accept: <variants> — fill-blank EXTRA accepted answers. Collected raw and
    // resolved in finalization: with explicit A–D options the correct option is
    // canonical and these are extras; without options the first is canonical.
    const mAccept = line.match(RE_ACCEPT);
    if (mAccept && currentQuestion) {
      currentQuestion.type = 'fill_blank';
      const variants = mAccept[1]
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
      const prev = acceptScratch.get(currentQuestion.tempId) ?? [];
      acceptScratch.set(currentQuestion.tempId, [...prev, ...variants]);
      continue;
    }

    // A) / B) … option line — for the open question, MC OR fill (cap at 4). feat-109.
    const mOpt = line.match(RE_OPTION);
    if (mOpt && currentQuestion) {
      if (currentQuestion.options.length < 4) {
        currentQuestion.options.push({
          letter: mOpt[1].toLowerCase() as CorrectOption,
          text: mOpt[2].trim(),
        });
      }
      continue;
    }

    // Bullet under Good/Bad things — a rehearsal criterion. feat-109: a
    // `- <label>: <description>` bullet splits on the first colon; a plain bullet
    // uses its text as BOTH label and description (never an empty description,
    // which the backend rejects with 422).
    const mBullet = line.match(RE_BULLET);
    if (mBullet && currentRehearsal && rehearsalSection) {
      const { label, description } = splitCriterionBullet(mBullet[1].trim());
      currentRehearsal.criteria.push({
        kind: rehearsalSection,
        label,
        description,
        position: currentRehearsal.criteria.length,
      });
      continue;
    }

    // Fallback: an unrecognized non-empty line inside an MC question that is
    // still collecting options (before Correct:, < 4 options) is treated as an
    // option with a proposed letter, and flagged (mc_option_no_letter).
    if (
      line.length > 0 &&
      currentQuestion &&
      currentQuestion.type === 'multiple_choice' &&
      currentQuestion.correct_option === null &&
      currentQuestion.options.length > 0 &&
      currentQuestion.options.length < 4
    ) {
      const letter = LETTERS[currentQuestion.options.length];
      currentQuestion.options.push({ letter, text: line });
      currentQuestion.needsReview.push({
        reason: 'mc_option_no_letter',
        severity: 'block',
        message: `Option "${truncate(line)}" had no A–D letter — we assigned ${letter.toUpperCase()}). Confirm or fix it.`,
      });
      continue;
    }
    // Anything else (blank lines, stray prose) is ignored.
  }

  // Finalization pass — resolve fill-blank canonical answer + accepted variants
  // from the explicit options and/or the collected `Accept:` variants (feat-109).
  for (const item of items) {
    if (item.kind === 'question' && item.type === 'fill_blank') {
      resolveFillBlank(item, acceptScratch.get(item.tempId) ?? []);
    }
    if (item.kind === 'reading') {
      const { body, imagesDropped } = normalizeReadingMarkdown(
        (readingRaw.get(item.tempId) ?? []).join('\n'),
      );
      item.body = body;
      item.imagesDropped = imagesDropped;
    }
  }

  // Validation pass — compute Needs-review flags per item.
  for (const item of items) {
    if (item.kind === 'question') {
      validateQuestion(item);
    } else if (item.kind === 'rehearsal') {
      validateRehearsal(item);
    } else {
      validateReading(item);
    }
  }

  const needsReview = items.flatMap((i) => i.needsReview);
  return { drills, items, needsReview };
}

// ============================================================
// Reading markdown normalization (feat-343)
// ============================================================

/** A line made only of one or more `![alt](url)` images. */
const RE_IMAGE_LINE = /^(?:!\[[^\]]*\]\([^)]*\)\s*)+$/;
const RE_IMAGE = /!\[[^\]]*\]\([^)]*\)/g;
/** `* ` / `• ` / `+ ` / `- ` bullet at any indent. `**bold**` is safe: the
 *  marker must be followed by whitespace. */
const RE_MD_BULLET = /^\s*[-*•+]\s+(.*)$/;
const RE_MD_HEADING = /^(#+)\s+(.*)$/;
const RE_MD_QUOTE = /^>\s?(.*)$/;
/** Segments the inline rewrites must NOT touch: code spans and link targets. */
const RE_INLINE_PROTECTED = /(`[^`]*`|\]\([^)]*\))/;

/**
 * Normalize Notion's plain-text markdown into the subset the Reading preview
 * (`ReadingDetailForm` `MarkdownPreview`) and Flutter `_MarkdownBody` render:
 * `#`/`##`/`###` headings, `- ` bullets, blank-line paragraphs, `**bold**`,
 * `*italic*`, `[label](url)`. Pure. Rules (spec.md §Formatting):
 *  - image-only lines are removed and counted;
 *  - `* `/`• `/`+ ` bullets → `- `, nested bullets flattened to top level;
 *  - `####`+ headings → `###`;
 *  - `__bold__` → `**bold**`, word-bounded `_italic_` → `*italic*`;
 *  - leading indentation stripped; `> ` quote markers dropped;
 *  - numbered lines and everything else pass through as text;
 *  - every heading line and every run of `- ` bullets becomes its OWN block
 *    (blank line inserted where Notion had none) — the renderers split blocks
 *    only on blank lines, so a heading or list glued to a paragraph would show
 *    as literal `###` / `- ` text (DESIGN-QUESTION Q1);
 *  - leading/trailing blank lines trimmed, 3+ newlines collapse to one blank line.
 */
export function normalizeReadingMarkdown(raw: string): { body: string; imagesDropped: number } {
  let imagesDropped = 0;
  const out: string[] = [];

  for (const rawLine of raw.replace(/\r/g, '').split('\n')) {
    const trimmed = rawLine.trim();

    if (trimmed && RE_IMAGE_LINE.test(trimmed)) {
      imagesDropped += (trimmed.match(RE_IMAGE) ?? []).length;
      continue;
    }

    let line: string;
    const mQuote = trimmed.match(RE_MD_QUOTE);
    const mBullet = rawLine.match(RE_MD_BULLET);
    const mHeading = trimmed.match(RE_MD_HEADING);
    if (mQuote) {
      line = mQuote[1].trim();
    } else if (mBullet) {
      line = `- ${mBullet[1].trim()}`;
    } else if (mHeading) {
      const level = Math.min(mHeading[1].length, 3);
      line = `${'#'.repeat(level)} ${mHeading[2].trim()}`;
    } else {
      line = trimmed;
    }
    out.push(rewriteInline(line));
  }

  // Block isolation: a heading is a one-line block; a bullet run is a block.
  const kindOf = (l: string): 'blank' | 'heading' | 'bullet' | 'text' =>
    !l ? 'blank' : /^#{1,3}\s/.test(l) ? 'heading' : l.startsWith('- ') ? 'bullet' : 'text';
  const isolated: string[] = [];
  out.forEach((l, i) => {
    if (i > 0) {
      const prev = kindOf(out[i - 1]);
      const cur = kindOf(l);
      const needsBreak =
        prev !== 'blank' &&
        cur !== 'blank' &&
        (prev === 'heading' || cur === 'heading' || (prev === 'bullet') !== (cur === 'bullet'));
      if (needsBreak) isolated.push('');
    }
    isolated.push(l);
  });

  const body = isolated
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+/, '')
    .replace(/\n+$/, '');
  return { body, imagesDropped };
}

/** `__bold__` → `**bold**`, `_italic_` → `*italic*`, outside code spans and
 *  link targets (so `snake_case` and `…/a_b_c` URLs survive). */
function rewriteInline(line: string): string {
  return line
    .split(RE_INLINE_PROTECTED)
    .map((seg, i) => {
      if (i % 2 === 1) return seg; // captured protected segment
      return seg
        .replace(/__([^\s_](?:[^_]*[^\s_])?)__/g, '**$1**')
        .replace(/(^|[^\w*])_([^\s_](?:[^_]*[^\s_])?)_(?!\w)/g, '$1*$2*');
    })
    .join('');
}

// ============================================================
// Validation (Needs-review rule set, ADR 0022 D4)
// ============================================================

function validateQuestion(q: ParsedQuestion): void {
  if (!q.question_text.trim()) {
    q.needsReview.push({
      reason: 'missing_required_field',
      severity: 'block',
      message: 'Question text is empty — add the question.',
    });
  }

  if (q.type === 'multiple_choice') {
    if (q.options.length < 2) {
      q.needsReview.push({
        reason: 'mc_too_few_options',
        severity: 'block',
        message: `Only ${q.options.length} option${q.options.length === 1 ? '' : 's'} found — multiple choice needs at least 2.`,
      });
    }
    // feat-346: options must be contiguous from A (the backend 422s a gap).
    const byLetter = ['a', 'b', 'c', 'd'].map(
      (l) => q.options.find((o) => o.letter === l)?.text ?? '',
    );
    const gap = mcOptionsProblem(byLetter);
    if (gap?.code === 'gap') {
      q.needsReview.push({ reason: 'mc_options_gap', severity: 'block', message: gap.message });
    }
    if (q.correct_option === null) {
      q.needsReview.push({
        reason: 'mc_no_correct_line',
        severity: 'block',
        message: 'No `Correct:` line found — choose the correct answer.',
      });
    }
  } else {
    // fill_blank: legal with no accepted answer, but worth a look. A fill-blank
    // is "answered" if it has any non-empty option (explicit A–D, or a canonical
    // synthesized from `Accept:`) OR extra accepted variants (feat-109).
    const hasOption = q.options.some((o) => o.text.trim().length > 0);
    const hasVariants = !!q.blankAcceptedVariants && q.blankAcceptedVariants.trim().length > 0;
    if (!hasOption && !hasVariants) {
      q.needsReview.push({
        reason: 'fill_no_accept',
        severity: 'warn',
        message: 'No accepted answer — add A–D options + `Correct:` or an `Accept:` line, or dismiss if the blank is open-ended.',
      });
    }
  }
}

/**
 * Resolve a fill-blank's canonical answer + extra accepted variants (feat-109).
 *  - With explicit A–D options: the `Correct:` option is the canonical answer
 *    and EVERY `Accept:` variant becomes an extra (canonical not repeated).
 *  - Without explicit options (legacy `Accept:`-only): the first `Accept:`
 *    variant is synthesized as option A (canonical) and the rest are extras.
 */
function resolveFillBlank(q: ParsedQuestion, accept: string[]): void {
  const hasExplicitOptions = q.options.some((o) => o.text.trim().length > 0);
  if (hasExplicitOptions) {
    const canonical = q.correct_option
      ? q.options.find((o) => o.letter === q.correct_option)?.text.trim() ?? ''
      : '';
    const extras = accept.filter(
      (v) => !canonical || v.toLowerCase() !== canonical.toLowerCase(),
    );
    q.blankAcceptedVariants = extras.length ? extras.join('\n') : null;
  } else if (accept.length > 0) {
    q.options = [{ letter: 'a', text: accept[0] }];
    q.correct_option = 'a';
    const extras = accept.slice(1);
    q.blankAcceptedVariants = extras.length ? extras.join('\n') : null;
  } else {
    q.blankAcceptedVariants = null;
  }
}

/**
 * Split a criterion bullet into `label` + `description`. A `label: description`
 * bullet splits on the first colon; a plain bullet uses its full text for BOTH
 * fields so neither is ever empty (the backend requires `description` ≥ 1 char,
 * which is the feat-109 import-422 fix). Either side falling empty falls back to
 * the other, then to the raw text.
 */
function splitCriterionBullet(text: string): { label: string; description: string } {
  const m = text.match(/^([^:]+):\s*(.+)$/);
  if (m) {
    const label = m[1].trim();
    const description = m[2].trim();
    return {
      label: label || description || text,
      description: description || label || text,
    };
  }
  return { label: text, description: text };
}

function validateRehearsal(r: ParsedRehearsal): void {
  if (!r.name.trim()) {
    r.needsReview.push({
      reason: 'missing_required_field',
      severity: 'block',
      message: 'Rehearsal name is empty — add a short name.',
    });
  }
  if (!r.role || !r.role.trim()) {
    r.needsReview.push({
      reason: 'rehearsal_missing_role',
      severity: 'block',
      message: 'No `Role:` line — a role is required (commit fails without it).',
    });
  }
  // feat-187 (D6): legal to save without one (the column is nullable), but the
  // rehearsal then ships with an empty "Try saying" card — so WARN, dismissible.
  if (!r.correct_example_response.trim()) {
    r.needsReview.push({
      reason: 'rehearsal_missing_correct_example',
      severity: 'warn',
      message:
        'No `Correct example:` line — the trainee sees an empty "Try saying" card. Add a model answer, or dismiss if this rehearsal is open-ended.',
    });
  }
}

/** feat-343: Needs-review for a Reading. Images dropped ⇒ warn (dismissible);
 *  empty body ⇒ block (the backend requires a non-empty body). An empty title is
 *  legal (the column is nullable) and raises nothing. */
function validateReading(r: ParsedReading): void {
  if (r.imagesDropped > 0) {
    r.needsReview.push({
      reason: 'reading_images_dropped',
      severity: 'warn',
      message: readingImagesDroppedMessage(r.imagesDropped),
    });
  }
  if (!r.body.trim()) {
    r.needsReview.push({
      reason: 'reading_empty_body',
      severity: 'block',
      message: READING_EMPTY_BODY_MESSAGE,
    });
  }
}

/** feat-343: shared copy so the parser and the Step-2 live recompute agree. */
export function readingImagesDroppedMessage(n: number): string {
  return `${n} image${n === 1 ? '' : 's'} from Notion ${n === 1 ? "wasn't" : "weren't"} imported — add ${n === 1 ? 'it' : 'them'} in the Reading editor after commit.`;
}

export const READING_EMPTY_BODY_MESSAGE =
  'Reading body is empty — add the text (a Reading needs a body).';

function truncate(s: string, max = 40): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
